/*
 *     Copyright (C) 2021 TART K.K.
 *
 *     This program is free software: you can redistribute it and/or modify
 *     it under the terms of the GNU General Public License as published by
 *     the Free Software Foundation, either version 3 of the License, or
 *     (at your option) any later version.
 *
 *     This program is distributed in the hope that it will be useful,
 *     but WITHOUT ANY WARRANTY; without even the implied warranty of
 *     MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *     GNU General Public License for more details.
 *
 *     You should have received a copy of the GNU General Public License
 *     along with this program.  If not, see https://www.gnu.org/licenses/.
 */

import React, { useCallback, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  Button,
  TextField,
  Grid,
  Stack,
  useMediaQuery,
} from "@mui/material";
import themes from "../../../themes";
import styled from "@emotion/styled";
import { BigNumberish, ethers } from "ethers";

export interface MultipleDialogProps {
  readonly open: boolean;
  readonly setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  readonly buyBundle: (...args: any[]) => Promise<void>;
  readonly price?: BigNumberish;
}

const MultipleDialog: React.FC<MultipleDialogProps> = ({
  buyBundle,
  ...props
}) => {
  const fullScreen = useMediaQuery("@media (max-width:340px)");
  const [amount, setAmount] = useState(2);
  const [isButtonPushed, setIsButtonPushed] = useState(false);
  const [idList, setIdList] = useState<number[]>([]);

  const p = ethers.utils.formatUnits(props.price ?? 0, 18);

  const onPurchaseButtonClick = useCallback(() => {
    buyBundle(idList, {
      value: ethers.utils.parseEther((Number(p) * idList.length).toString()),
    });
    props.setModalOpen(false);
  }, [buyBundle, idList, p]);

  const onOkButtonClick = () => {
    setIsButtonPushed(true);
  };

  const onClickReloadButton = () => {};

  const onCancelButtonClick = useCallback(() => {
    props.setModalOpen(false);
  }, [props.setModalOpen]);

  const content = () => {
    if (isButtonPushed) {
      return (
        <>
          <GridWrapper>
            <Grid container spacing={1}>
              {idList.map((id) => {
                return (
                  <Grid item xs={12} sm={6} key={id}>
                    <img
                      src={`https://d1ugk7uy785ker.cloudfront.net/${id}.png`}
                      alt={"NFT"}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </GridWrapper>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ marginBottom: 1 }}
          >
            <Button
              color="primary"
              variant="outlined"
              fullWidth
              disableElevation
              onClick={() => {
                setIsButtonPushed(false);
              }}
            >
              back
            </Button>
            <Button
              color="primary"
              variant="outlined"
              fullWidth
              disableElevation
              onClick={onClickReloadButton}
            >
              Reload
            </Button>
          </Stack>
          <div>
            <Button
              color="primary"
              variant="contained"
              fullWidth
              disableElevation
              onClick={onPurchaseButtonClick}
            >
              Mint
            </Button>
          </div>
        </>
      );
    } else {
      return (
        <>
          <StyledDialogTitle>What is the order quantity?</StyledDialogTitle>
          <Box m={2}>
            <TextField
              value={amount}
              variant={"standard"}
              onChange={(e) => {
                setAmount(Number(e.target.value));
              }}
              fullWidth
              size="small"
              helperText="Please enter a number between 2 and 10."
              inputProps={{ max: 10, min: 2, type: "number" }}
            />
          </Box>
          <div>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ marginBottom: 1 }}
            >
              <Button
                color="primary"
                variant="outlined"
                fullWidth
                disableElevation
                onClick={onCancelButtonClick}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                disableElevation
                disabled={amount <= 1 || amount >= 11}
                onClick={onOkButtonClick}
                style={{ color: "white" }}
              >
                OK
              </Button>
            </Stack>
          </div>
        </>
      );
    }
  };

  return (
    <Dialog
      aria-labelledby="simple-dialog-title"
      open={props.open}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        square: true,
      }}
    >
      <Container>{content()}</Container>
    </Dialog>
  );
};

export default MultipleDialog;

const Container = styled.div`
  color: #000;
  padding: ${themes.spacing(2)};
  border: 1px solid #01b9ff;
  @media (max-width: 340px) {
    border: none;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  padding: 0;
  text-align: center;
  text-transform: uppercase;
  color: #01b9ff;
`;

export const GridWrapper = styled.div`
  overflow: auto;
  max-height: 67vh;
  margin-bottom: 8px;
`;
